import React, { useRef, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';

// Variants to stagger the appearance of the child text elements.
const containerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 1, // Delay between animations of each child element.
        },
    },
};

// First text remains as grey, stays smaller.
const firstTextVariants = {
    hidden: { opacity: 1, fontSize: "2rem", color: "grey" },
    visible: { opacity: 1, fontSize: "3.5rem", color: "grey", transition: { duration: 1 } },
};

// Second text now grows bigger and turns to primary color.
const secondTextVariants = {
    hidden: { opacity: 0, fontSize: "2rem", color: "grey" },
    visible: { opacity: 1, fontSize: "4.5rem", color: "#6A5AE0", transition: { duration: 1 } },
};

const ScrollRevealSection = () => {
    const sectionRef = useRef(null);
    const controls = useAnimation();

    useEffect(() => {
        const observerOptions = {
            root: null,
            threshold: 0.95, // almost fully visible
        };

        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
                // Lock scrolling while animations play.
                document.body.style.overflow = 'hidden';

                // Start the container's animation; children will animate in a staggered manner.
                controls.start("visible").then(() => {
                    // After animations complete, allow scrolling.
                    setTimeout(() => {
                        document.body.style.overflow = '';
                    }, 500);
                });
            }
        }, observerOptions);

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            document.body.style.overflow = ''; // Ensure scroll is unlocked on unmount.
            if (sectionRef.current) observer.disconnect();
        };
    }, [controls]);

    return (
        <section
            ref={sectionRef}
            className="min-h-screen flex items-center justify-center bg-white py-16"
        >
            <div className="container mx-auto px-4 max-w-4xl text-center">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate={controls}
                    className="flex flex-col items-center"
                >
                    <motion.h2
                        variants={firstTextVariants}
                        className="font-black"
                    >
                        Exam Prep Is Hard. Unless...
                    </motion.h2>
                    <motion.h2
                        variants={secondTextVariants}
                        className="font-black mt-8"  // Increased spacing
                    >
                        You Do It The Fun Way
                    </motion.h2>
                    <div className="relative h-1 mx-auto mt-4 max-w-[80%] bg-gray-200">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: '100%' }}
                            transition={{ duration: 2.8, delay: 1.5, ease: 'easeOut' }}
                            className="h-full bg-[#6A5AE0]"
                        />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default ScrollRevealSection;